import * as React from 'react';
import useI18n from 'src/hooks/useI18n';
import moment from 'moment';
import partial from 'lodash/partial';

import {classify} from 'src/utils/index';
import {resolveDateRangeWithTimezone, type Range} from 'src/utils/date-range';
import {SimpleDateRangePicker} from 'src/components/lib/date-range-picker/date-range-picker';
import Select, {type SelectOption} from 'src/components/lib/select/select';
import withAnalyticsQuery, {
  type AnalyticsQueryProps,
} from 'src/components/analytics/with-query';

import css from './date-range.css';

import {useReleaseFlag} from 'src/hooks/product-flags';


const getTimeAgoOptions = (t: any) => [
  {
    value: JSON.stringify({
      days: 90,
    }),
    label: t('Last_90_days', 'Last 90 days'),
  },
  {
    value: JSON.stringify({
      days: 30,
    }),
    label: t('Last_30_days', 'Last 30 days'),
  },
  {
    value: JSON.stringify({
      days: 7,
    }),
    label: t('Last_7_days', 'Last 7 days'),
  },
  {
    value: 'range',
    label: `${t('Range', 'Range')}...`,
  },
];

type OwnProps = {
  className?: string;
  range?: Range;
  onTimeAgoChange?: (
    props: Props,
    selectValue?:
      | Readonly<{
          value: string;
        }>
      | null
      | undefined,
  ) => unknown;
  onDateRangeChange?: (props: Props, range: Range) => unknown;
  defaultTimeAgo?: string;
  otherTimeAgoOptions?: {
    value: string;
    label: string;
  }[];
  minDate?: string;
};

type Props = AnalyticsQueryProps & OwnProps;

const DateRange = (props: Props) => {
  const {labelI18nInstance} = useI18n();
  const t = labelI18nInstance.t;
  const timeAgoOptions = getTimeAgoOptions(t);
  const {
    query,
    onTimeAgoChange = handleTimeAgoChange,
    onDateRangeChange = handleChange,
    defaultTimeAgo = _defaultTimeAgo,
    minDate,
  } = props;
  const useRange = Boolean(query.start_date && query.end_date);
  const timeAgo = useRange ? 'range' : query.time_ago || defaultTimeAgo;
  const isDefaultTimeRangeEnabled = useReleaseFlag(
    'analyticsDefaultTimeRangeEnabled',
  );
  if (isDefaultTimeRangeEnabled && timeAgoOptions.length === 4) {
    timeAgoOptions.splice(3, 0, {
      value: JSON.stringify({
        days: 1,
      }),
      label: t('Current_Day', 'Current Day'),
    });
  }
  const {
    range = {
      startDate: query.start_date,
      endDate: query.end_date,
    },
  } = props;

  return (
    <div
      className={classify(
        css.dateRange,
        {[css.manual]: useRange},
        props.className,
      )}
    >
      {/* @ts-expect-error - TS2769 - No overload matches this call. */}
      <Select
        className={css.timeAgoSelect}
        options={
          props.otherTimeAgoOptions
            ? [...props.otherTimeAgoOptions, ...timeAgoOptions]
            : timeAgoOptions
        }
        value={timeAgo}
        // @ts-expect-error - TS2345 - Argument of type 'Props' is not assignable to parameter of type 'AnalyticsQueryProps & OwnProps & { query: any; onQueryChange: any; defaultTimeAgo: any; }'.
        onChange={(timeAgo) => onTimeAgoChange(props, timeAgo)}
        searchable={false}
      />
      {useRange && (
        // @ts-expect-error - TS2769 - No overload matches this call.
        <SimpleDateRangePicker
          value={range}
          onChange={partial(onDateRangeChange, props)}
          maxDate={moment()}
          minDate={minDate ? moment(minDate) : ''}
        />
      )}
    </div>
  );
};

export default withAnalyticsQuery(DateRange) as React.ComponentType<OwnProps>;

const _defaultTimeAgo = JSON.stringify({
  days: 90,
});

function handleTimeAgoChange(
  // @ts-expect-error - TS7031 - Binding element 'query' implicitly has an 'any' type. | TS7031 - Binding element 'onQueryChange' implicitly has an 'any' type. | TS7031 - Binding element 'defaultTimeAgo' implicitly has an 'any' type.
  {query, onQueryChange, defaultTimeAgo},
  selectValue: SelectOption<string> | null | undefined,
) {
  const {value} = selectValue || {};
  let update;
  if (value === 'range') {
    const timeAgo = defaultTimeAgo ? JSON.parse(defaultTimeAgo) : undefined;
    update = {
      // NOTE (kyle): commands are run in order. resolveDateRange() sometimes
      // returns a query with time_ago.
      $merge: resolveDateRangeWithTimezone(query, undefined, timeAgo),
      $unset: 'time_ago',
    };
  } else {
    update = {
      $unset: ['start_date', 'end_date'],
      time_ago: {$set: value},
    };
  }
  onQueryChange(update);
}

// @ts-expect-error - TS7031 - Binding element 'startDate' implicitly has an 'any' type. | TS7031 - Binding element 'endDate' implicitly has an 'any' type.
function handleChange({onQueryChange}: Props, {startDate, endDate}) {
  // @ts-expect-error - TS2554 - Expected 2 arguments, but got 1.
  onQueryChange({
    start_date: {$set: startDate},
    end_date: {$set: endDate},
  });
}
